import { MenuItem, Select, TextField } from '@mui/material';
import {
  RiskDateFieldOption,
  RiskTierDirection,
  RiskTierName
} from '../../../Api/Admin/ProjectManagementConfigs/projectManagementConfigsApi';
import { RiskDateFieldSelect } from './RiskDateFieldSelect';
import { measuresOneDateAgainstItself, TierDateSides } from './riskTierDateFields';
import { parseDayThreshold, thresholdPhrase, tierName, TIER_CIRCLE_COLORS } from './riskTierPresentation';

export interface RiskTierFormRow {
  tier: RiskTierName;
  label: string;
  thresholdText: string;
  direction: RiskTierDirection;
  primaryDateField: string;
  comparisonDateField: string;
}

export interface RiskTierRowProps {
  row: RiskTierFormRow;
  dateFields: RiskDateFieldOption[];
  sides: TierDateSides;
  // The worst past tier on its pair, which the API runs on past its maximum.
  runsOnPastMax: boolean;
  onChange: (patch: Partial<RiskTierFormRow>) => void;
}

export const RiskTierRow = ({ row, dateFields, sides, runsOnPastMax, onChange }: RiskTierRowProps) => {
  const range = parseDayThreshold(row.thresholdText);
  const phrase = thresholdPhrase(range, row.direction, row.tier, runsOnPastMax);
  const sameDate = measuresOneDateAgainstItself(row);
  const name = tierName(row);

  return (
    <div
      className="flex flex-col gap-3 py-5 border-t border-gray-200"
      data-cy={`risk-tier-row-${row.tier}`}
      data-testid={`risk-tier-row-${row.tier}`}>
      <div className="flex items-center gap-8">
        <div className="flex flex-col -mr-4">
          <span
            aria-hidden="true"
            className="mb-1 h-5"
          />
          <span className="flex h-10 items-center">
            <span
              className="w-5 h-5 rounded-full shrink-0"
              style={{ backgroundColor: TIER_CIRCLE_COLORS[row.tier] }}
              data-cy={`risk-tier-dot-${row.tier}`}
            />
          </span>
        </div>
        <div className="flex flex-col">
          <label
            htmlFor={`risk-label-${row.tier}`}
            className="text-sm font-medium text-gray-700 mb-1">
            Risk Label
          </label>
          <TextField
            id={`risk-label-${row.tier}`}
            size="small"
            className="w-56"
            value={row.label}
            onChange={(e) => onChange({ label: e.target.value })}
            InputProps={{ style: { fontSize: '.875rem' } }}
            data-cy={`risk-label-input-${row.tier}`}
          />
        </div>
        <div className="flex flex-col">
          <label
            htmlFor={`risk-threshold-${row.tier}`}
            className="text-sm font-medium text-gray-700 mb-1">
            Day Threshold
          </label>
          <TextField
            id={`risk-threshold-${row.tier}`}
            size="small"
            className="w-40"
            value={row.thresholdText}
            error={range === null}
            onChange={(e) => onChange({ thresholdText: e.target.value })}
            InputProps={{ style: { fontSize: '.875rem' } }}
            data-cy={`risk-threshold-input-${row.tier}`}
          />
        </div>
        {row.tier !== 'green' && (
          <div className="flex flex-col">
            <label
              id={`risk-direction-label-${row.tier}`}
              htmlFor={`risk-direction-${row.tier}`}
              className="text-sm font-medium text-gray-700 mb-1">
              Past or Before
            </label>
            <Select
              labelId={`risk-direction-label-${row.tier}`}
              inputProps={{ id: `risk-direction-${row.tier}` }}
              size="small"
              className="w-36"
              value={row.direction}
              onChange={(e) => onChange({ direction: e.target.value as RiskTierDirection })}
              style={{ fontSize: '.875rem' }}
              data-cy={`risk-direction-select-${row.tier}`}>
              <MenuItem value="past">Past</MenuItem>
              <MenuItem value="before">Before</MenuItem>
            </Select>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 flex-wrap pl-9">
        <RiskDateFieldSelect
          id={`risk-primary-date-${row.tier}`}
          ariaLabel={`Primary date for the ${name} tier`}
          value={row.primaryDateField}
          options={dateFields}
          orphanLabel={sides.primary.orphanLabel}
          unavailable={sides.primary.unavailable}
          error={sameDate}
          onChange={(primaryDateField) => onChange({ primaryDateField })}
          dataCy={`risk-primary-date-select-${row.tier}`}
        />
        <span
          className="text-sm text-gray-500"
          data-cy={`risk-preview-${row.tier}`}
          data-testid={`risk-phrase-${row.tier}`}>
          {phrase}
        </span>
        <RiskDateFieldSelect
          id={`risk-comparison-date-${row.tier}`}
          ariaLabel={`Comparison date for the ${name} tier`}
          value={row.comparisonDateField}
          options={dateFields}
          orphanLabel={sides.comparison.orphanLabel}
          unavailable={sides.comparison.unavailable}
          error={sameDate}
          onChange={(comparisonDateField) => onChange({ comparisonDateField })}
          dataCy={`risk-comparison-date-select-${row.tier}`}
        />
        {/* Cancels the flex gap so the full stop sits against the picker. */}
        {phrase && <span className="text-sm text-gray-500 -ml-1.5">.</span>}
      </div>
    </div>
  );
};
